/**
 * Shared validation for player / host names. Names entered here are now visible to strangers in
 * public lobbies and the public game browser ("Hosted by …"), so every name that reaches the
 * network must clear the same checks: length, no Firebase-path-breaking slashes, and a profanity
 * screen.
 *
 * This is a best-effort client-side filter — there's no Cloud Function tier to enforce it
 * server-side — so a determined user on a modified client can still get a bad name through. The
 * goal is to stop casual abuse and keep the public browser presentable, while accepting that
 * profanity filtering is inherently imperfect (slang evolves, and over-blocking creates the
 * "Scunthorpe problem" where innocent names contain banned substrings).
 *
 * Matching strategy (see [isProfane]):
 *  - Input is lowercased, leet-folded (4→a, 1→i, $→s, …), and repeated runs are collapsed
 *    ("fuuuck" → "fuck") so common evasions normalize to the same form.
 *  - [SEVERE] terms are matched as substrings of the collapsed string — aggressive, because these
 *    terms (hard profanity, slurs, explicit sexual terms) essentially never appear inside ordinary
 *    names. A few short ones (e.g. "cunt") can still trip rare place/surnames like "Scunthorpe";
 *    that's an accepted trade-off for catching the word.
 *  - [COLLISION_PRONE] terms are matched only as whole tokens, so legitimate names and words that
 *    contain them as substrings pass: Dick, Hancock, Pakistani, raccoon, analysis, Cassidy.
 */
object PlayerNameValidator {
    const val MAX_LENGTH = 24

    sealed interface Result {
        /** The accepted, trimmed name to persist/send. */
        data class Valid(val name: String) : Result
        /** Human-readable reason to surface to the player. */
        data class Invalid(val reason: String) : Result
    }

    fun validate(raw: String): Result {
        val name = raw.trim()
        return when {
            name.isEmpty() ->
                Result.Invalid("Enter a name before continuing.")
            name.length > MAX_LENGTH || '/' in name ->
                Result.Invalid("Use a name up to $MAX_LENGTH characters without slashes.")
            isProfane(name) ->
                Result.Invalid("Please choose a different name.")
            else ->
                Result.Valid(name)
        }
    }

    fun isProfane(raw: String): Boolean {
        val folded = leetFold(raw)
        val letters = folded.filter { it in 'a'..'z' }

        // "kkk" can't go through the collapse path (it would reduce to "k" and match any name with
        // a k), so match it directly on the spacing-stripped letters ("k k k" → "kkk").
        if ("kkk" in letters) return true

        // Severe terms: substring match against the repeat-collapsed form so spacing and stretching
        // tricks ("f u c k", "shiiit", "fuuuck") collapse to the same needle.
        val collapsed = collapseRuns(letters)
        if (collapsed.isNotEmpty() && SEVERE_COLLAPSED.any { collapsed.contains(it) }) return true

        // Collision-prone terms: only flag when they stand alone as a token, so "Dick", "Cockburn",
        // "Pakistani", "raccoon" and "analysis" are not falsely rejected.
        val tokens = folded.split(*SEPARATORS).filter { it.isNotEmpty() }
        return tokens.any { it in COLLISION_PRONE }
    }

    /** Lowercases and maps common leetspeak/symbol substitutions to letters. */
    private fun leetFold(raw: String): String {
        val sb = StringBuilder(raw.length)
        for (c in raw.lowercase()) {
            sb.append(
                when (c) {
                    '4', '@' -> 'a'
                    '8' -> 'b'
                    '3', '€' -> 'e'
                    '1', '!', '|' -> 'i'
                    '0' -> 'o'
                    '5', '$' -> 's'
                    '7', '+' -> 't'
                    '9' -> 'g'
                    else -> c
                }
            )
        }
        return sb.toString()
    }

    /** Collapses runs of the same character to one: "fuuuuck" → "fuck", "niiigga" → "niga". */
    private fun collapseRuns(s: String): String {
        if (s.length < 2) return s
        val sb = StringBuilder(s.length)
        for (c in s) if (sb.isEmpty() || sb[sb.length - 1] != c) sb.append(c)
        return sb.toString()
    }

    private val SEPARATORS = charArrayOf(
        ' ', '\t', '\n', '_', '-', '.', ',', '*', '#', '~', '`', '\'', '"',
        '(', ')', '[', ']', '{', '}', '<', '>', ':', ';', '?', '/', '\\',
        '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', // leetFold maps most of these, but bare digits split tokens
    )

    // Hard profanity, slurs, and explicit sexual terms. Substring-matched (after collapsing), so
    // include unambiguous compound forms ("dickhead", "asshole") here rather than short colliding
    // roots ("dick", "ass"), which live in COLLISION_PRONE.
    private val SEVERE: List<String> = listOf(
        // Profanity & vulgar insults
        "fuck", "motherfucker", "fucker", "fuckface", "fuckwit", "fuckboy", "fuckhead",
        "fuckoff", "clusterfuck", "shit", "bullshit", "shithead", "shitbag", "dipshit",
        "cunt", "bitch", "sonofabitch", "bastard", "asshole", "asshat", "asswipe",
        "dumbass", "jackass", "dickhead", "dickface", "cocksucker", "cockhead",
        "douchebag", "douche", "twat", "wanker", "bollocks", "scumbag",
        "slut", "whore", "skank", "stfu",
        // Explicit sexual
        "dildo", "blowjob", "handjob", "rimjob", "cumshot", "creampie", "gangbang",
        "bukkake", "hentai", "masturbat", "ejaculat", "cunnilingus", "fellatio",
        "felching", "jizz", "queef", "smegma", "deepthroat", "fingerbang",
        "porn", "fleshlight", "buttplug", "buttsex", "ballsack", "nutsack",
        "scrotum", "clitoris", "pussy", "penis", "vagina", "boner", "molest", "incest",
        "bestiality", "pedophile", "paedophile", "childporn",
        // Racial / ethnic slurs
        "nigger", "nigga", "niglet", "chink", "gook", "kike", "beaner", "wetback",
        "jigaboo", "sambo", "sandnigger", "towelhead", "raghead", "zipperhead",
        "chinaman", "tarbaby", "darkie", "spearchucker", "porchmonkey", "cottonpicker",
        "redskin", "injun",
        // Homophobic / transphobic slurs
        "faggot", "faggy", "fagget", "tranny", "shemale", "ladyboy",
        // Ableist slurs (incl. the "regarded" algospeak evasion of the r-slur)
        "retard", "retarded", "regarded", "spastic", "mongoloid", "windowlicker",
        // Hate / extremism ("kkk" is handled separately in isProfane — see note there)
        "nazi", "hitler", "heilhitler", "kluklux", "klansman", "whitepower",
        "whitepride", "gaschamber", "lynchmob",
    )

    // Precompute the collapsed form of each needle so it matches the collapsed haystack ("nigga" →
    // "niga" matches "niiigga" → "niga"). Without this, doubled letters in a needle never match.
    private val SEVERE_COLLAPSED: List<String> = SEVERE.map { collapseRuns(it) }

    // Short, offensive roots that also appear inside innocent words/names. Whole-token match only.
    private val COLLISION_PRONE: Set<String> = setOf(
        "ass", "dick", "dicks", "cock", "cocks", "tit", "tits", "titty", "anal",
        "arse", "fag", "fags", "homo", "dyke", "spic", "spics", "paki", "pakis",
        "jap", "japs", "coon", "coons", "wop", "wops", "hoe", "hoes", "cum", "cums",
        "slag", "wog", "prick", "hooker",
        // rape/rapist as whole tokens only — substring would block "therapist", "scraping", "grape".
        "rape", "raped", "rapes", "raping", "rapist",
    )
}
